import {
  Autocomplete,
  TextField,
  alpha,
  MenuItem,
  Checkbox,
  Typography,
  Chip,
  useTheme
} from '@mui/material'
import colors from 'styles/colors'
import RowStack from '../container/RowStack'
import CloseIcon from '@mui/icons-material/Close'
import { IconContainer } from '../Icons'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useTranslation } from 'react-i18next'

const StyledAutoComplete = ({
  options,
  selectedOptions,
  onSelection,
  placeholder,
  searchable = true,
  limitTags = 2,
  ...otherProps
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const placeholderText = placeholder || t('fullReport.filter')

  const RenderTag = ({
    tag,
    isAll = false,
    showClear = true,
    ...otherProps
  }) => {
    const onDelete = () => {
      onSelection(selectedOptions.filter(item => item.value !== tag))
    }
    return (
      <Chip
        {...otherProps}
        size='medium'
        variant={'outlined'}
        label={
          <Typography variant='body1Small' sx={{ fontWeight: 500 }}>
            {tag}
          </Typography>
        }
        deleteIcon={
          <CloseIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        }
        onDelete={!isAll && showClear ? onDelete : undefined}
        sx={{
          border: `1px solid ${!isAll ? colors.MutedTeal400 : colors.Grey200}`,
          backgroundColor: colors.MetalGrey50,
          height: 'auto',
          paddingX: 3,
          paddingY: 1,
          '& .MuiChip-label': {
            padding: 0,
            lineHeight: '120%',
            maxWidth: '100px', // Set max width for individual chips
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          },
          '& .MuiChip-deleteIcon': {
            padding: 0,
            margin: 0,
            fontSize: 16,
            color: theme.palette.primary.main
          }
        }}
        style={{
          margin: 0
        }}
      />
    )
  }

  const RenderTags = ({ selected, getTagProps }) => {
    let newSelected = selected
    if (newSelected?.length > limitTags) {
      newSelected = newSelected.slice(0, limitTags)
    }

    return (
      <RowStack spacing={0} gap={2}>
        <Typography variant='body1Small'>{placeholderText}:</Typography>
        {selected?.length > 0 ? (
          <>
            {newSelected.map((option, index) => (
              <RenderTag
                tag={option.label}
                key={`tag${index}`}
                {...getTagProps({ index })}
              />
            ))}
            {selected.length > limitTags && (
              <RenderTag
                tag={`+${selected.length - limitTags}`}
                showClear={false}
              />
            )}
          </>
        ) : (
          <RenderTag tag={t('settings.all')} isAll={true} />
        )}
      </RowStack>
    )
  }

  const FilterMenuItemSx = {
    '&.MuiAutocomplete-option': {
      padding: 0,
      paddingRight: 5,
      overflow: 'hidden',
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: colors.White
      },
      '&:hover': {
        backgroundColor: alpha(colors.Teal400, 0.1)
      }
    }
  }

  return (
    <Autocomplete
      multiple
      options={options}
      disableCloseOnSelect
      disableClearable
      popupIcon={<IconContainer icon={<KeyboardArrowDownIcon />} />}
      renderOption={(props, option, { selected }) => (
        <MenuItem
          {...props}
          value={option.value}
          key={`filter-${option.value}`}
          sx={FilterMenuItemSx}>
          <Checkbox checked={selected} />
          {option.label}
        </MenuItem>
      )}
      renderTags={(value, getTagProps) => (
        <RenderTags selected={value} getTagProps={getTagProps} />
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={
            selectedOptions.length || params.inputProps.value.length ? (
              ''
            ) : (
              <RenderTags />
            )
          }
          slotProps={{
            inputLabel: {
              shrink: false, // Prevent shrinking of label
              sx: {
                transform: 'none',
                top: 3,
                left: 16
              }
            }
          }}
          data-testid='filter-input'
        />
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      fullWidth
      value={selectedOptions}
      onChange={(e, value) => onSelection(value)}
      sx={{
        '& .MuiInputBase-root.MuiOutlinedInput-root': {
          height: 33,
          minWidth: 150,
          gap: 4,
          padding: '8px 10px 8px 16px',
          backgroundColor: colors.White,
          borderRadius: 25,
          flexWrap: 'nowrap'
        },
        '& .MuiInputBase-input.MuiOutlinedInput-input': {
          padding: 0,
          minWidth: 15
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${colors.MidnightTeal400}`
        },
        '& .MuiAutocomplete-endAdornment': {
          position: 'unset',
          transform: 'none'
        }
      }}
      slotProps={{
        paper: {
          sx: {
            width: 'max-content',
            minWidth: 150,
            borderRadius: 1,
            '& .MuiAutocomplete-listbox': {
              padding: 0
            }
          }
        }
      }}
      {...otherProps}
    />
  )
}

export default StyledAutoComplete
